import { Button } from "antd";
import SessionTimeline from "components/session/SessionTimeline";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { MESSAGE_TYPES } from "redux/windowManager";
import { accessTokenLogin, goToBounds, receiveAccessToken, routeSelected, wsWidgetPing } from "../../redux/actions/index";
import MapComponent from "../map/MapComponent";
import LoadingOverlay from "../util/LoadingOverlay";
import ChartV2Container from "./ChartV2Container";
import ExceedencesPage from "./ExceedencesPage";
import LocationWidget from "./LocationWidget";
import Pantovision from "./Pantovision/Pantovision";
import RailDetailWidget from "./RailDetailWidget";
import SidekickPlayer from "./SidekickPlayer";
import Shoecam from "./Shoecam/Shoecam";

const accessTokenSelector = (state) => state.access_token;
const dashboardDataSelector = (state) => {
    return _.get(state.widgetData.DASHBOARD, [state.dashboardWidgetKey, "state"], {});
};
const sessionsSelector = (state) => state.sessions;

export const widgetsConfig = {
    map: { title: "Route Map", limit: 1 },
    "rail-detail": { title: "Detailed Rail Images", limit: 1 },
    "track-geom": { title: "Track Geometry", limit: 1 },
    "track-geom-v2": { title: "Track Geometry", limit: 1 },
    exceedences: { title: "Exceedences", limit: 1 },
    location: { title: "Location Information", limit: 1 },
    stills: { title: "Stills Player", limit: 10 },
    timeline: { title: "Timeline", limit: 1 },
    "ole-geometry": { title: "OLE Geometry", limit: 1 },
    "conductor-shoe-geometry": { title: "Conductor Shoe Geometry", limit: 1 },
};

function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
}

const Sidekick = ({ match }) => {
    const dispatch = useDispatch();
    const { widgetType } = useParams();
    const query = useQuery();
    let widgetPingInterval = useRef;

    const [initialised, setInitialised] = useState(false);
    const [invalidToken, setInvalidToken] = useState(false);
    const [userAcknowledgedWarning, setUserAcknowledgedWarning] = useState(false);
    const [stillsSourceIndex, setStillsSourceIndex] = useState();

    const accessToken = useSelector(accessTokenSelector);
    const dashboardData = useSelector(dashboardDataSelector);
    const sessions = useSelector(sessionsSelector);

    useEffect(() => {
        dispatch({
            type: "CONNECT_TO_PARENT",
        });

        window.widget = {
            type: widgetType,
            id: query.get("id"),
        };
    }, [dispatch, query, widgetType]);

    const handleUnload = useCallback(() => {
        window.opener.postMessage({ type: MESSAGE_TYPES.CHILD_UNMOUNTED, widget: { type: widgetType, id: query.get("id") } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);

    useEffect(() => {
        if (!window.opener) {
            window.close();
        }

        window.addEventListener("beforeunload", handleUnload);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const currentSession = useMemo(() => {
        return sessions[dashboardData.routeID];
    }, [sessions, dashboardData.routeID]);

    const streams = useMemo(() => _.get(currentSession, ["stream_info"], []), [currentSession]);
    const stream = useMemo(() => {
        if (stillsSourceIndex <= streams.length - 1) {
            return streams[stillsSourceIndex];
        }
    }, [stillsSourceIndex, streams]);

    useEffect(() => {
        if (widgetType === "stills") {
            const sourceNumber = Number(query.get("source"));
            const streamIndex = _.findIndex(streams, { stream_index: sourceNumber });
            if (streamIndex > -1) {
                setStillsSourceIndex(streamIndex);
            }
        }
    }, [query, streams, widgetType]);

    useEffect(() => {
        let title = "AIVR";

        if (dashboardData.routeID) {
            title += ` #${dashboardData.routeID}`;
        }

        if (widgetsConfig[widgetType]) {
            title += `: ${widgetsConfig[widgetType].title}`;
        }

        if (widgetType === "stills" && stream) {
            const label = stream.label || stream.type || "";

            if (label) {
                title += ` (${label.trim()})`;
            }
        }

        document.title = title;
    }, [dashboardData, stream, widgetType]);

    useEffect(() => {
        if (match.params.token) {
            dispatch(receiveAccessToken(match.params.token));
        }
    }, [dispatch, match.params.token]);

    const validateAccessToken = useCallback(() => {
        dispatch(
            accessTokenLogin(accessToken, (success) => {
                if (!success) {
                    // if login/validate failed then logout and go to index
                    setInvalidToken(true);
                }
                setInitialised(true);
            }),
        );
    }, [accessToken, dispatch]);

    useEffect(() => {
        if (dashboardData.routeID) {
            dispatch(routeSelected(dashboardData.routeID, 0));
        } else {
            dispatch(routeSelected(null));
        }
    }, [dashboardData.routeID, dispatch]);

    useEffect(() => {
        if (currentSession && dashboardData.routeID) {
            dispatch(goToBounds(dashboardData.routeID));
        }
    }, [currentSession, dashboardData.routeID, dispatch]);

    useEffect(() => {
        if (accessToken) {
            validateAccessToken();
            widgetPingInterval.current = setInterval(() => {
                dispatch(wsWidgetPing(widgetType));
            }, 5000);
        }

        return () => {
            clearInterval(widgetPingInterval.current);
        };
    }, [dispatch, accessToken, validateAccessToken, widgetPingInterval, widgetType]);

    let content = <div className="EmptyGeometryWarning">Select a session in the main window to view data</div>;

    const makeUserAcknowledgedWarning = useMemo(
        () => (
            <div className="w-full h-full flex">
                <div className="widget__Info center">
                    <p>This geometry data is unvalidated and should be used for informative purposes only, not for operational decision making</p>
                    <Button
                        className="smallButton"
                        type="primary"
                        onClick={() => setUserAcknowledgedWarning(true)}>
                        I understand
                    </Button>
                </div>
            </div>
        ),
        [],
    );

    if (!initialised) {
        return <LoadingOverlay loading={true} />;
    }

    if (dashboardData.routeID) {
        switch (widgetType) {
            case "map":
                content = <MapComponent stripped="sidekick" />;
                break;
            case "rail-detail":
                content = <RailDetailWidget />;
                break;
            case "track-geom-v2":
                content = !userAcknowledgedWarning ? makeUserAcknowledgedWarning : <ChartV2Container />;
                break;
            case "exceedences":
                content = <ExceedencesPage />;
                break;
            case "location":
                content = <LocationWidget />;
                break;
            case "stills":
                content = (
                    <SidekickPlayer
                        sourceIndex={stillsSourceIndex}
                        setSourceIndex={setStillsSourceIndex}
                    />
                );
                break;
            case "timeline":
                content = <SessionTimeline />;
                break;
            case "ole-geometry":
                content = <Pantovision />;
                break;
            case "conductor-shoe-geometry":
                content = <Shoecam />;
                break;
            default:
                break;
        }
    }

    if (invalidToken) {
        content = (
            <div className="w-full h-full flex">
                <div className="widget__Info center">
                    <p>This widget is no longer active. Please close this window.</p>
                </div>
            </div>
        );
    }

    if (widgetType === "track-geom-v2") {
        return content;
    }

    let extraClasses = "";

    if (widgetType) {
        if (widgetType === "map") {
            extraClasses = "no-overflow";
        }
        if (widgetType === "exceedences") {
            extraClasses = "no-body-overflow";
        }
    }

    return (
        <div className={`sidekick ${extraClasses}`}>
            <div className="sidekick__Body">{content}</div>
        </div>
    );
};

export default Sidekick;
